import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Home, Download, FileSpreadsheet, CheckCircle, Send, AlertCircle, CheckCircle2, XCircle, Bot, TriangleAlert, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHelpers";
import SurveyVerificationButton from "@/components/SurveyVerificationButton";
import AIReviewPanel from "@/components/AIReviewPanel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const showBillingToast = (billing) => {
  if (!billing) return;
  if (billing.charged) {
    const amount = billing?.transaction?.amount ?? billing?.amount ?? 1;
    toast.success(`Wallet charged — ${amount} credit${Number(amount) === 1 ? '' : 's'} deducted`);
  } else if (billing.billing_status === 'already_charged') {
    toast.info('Already paid — regeneration is free');
  } else if (billing.billing_status === 'skipped_non_production') {
    console.info('Wallet skipped in non-production/test environment');
  } else if (billing.billing_status === 'error') {
    toast.warning(billing.message || 'Opinion regenerated, but wallet billing needs review');
  }
};

const DownloadPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const serviceBCaseId = new URLSearchParams(location.search).get('service_b_case_id');
  const serviceBRunNumber = new URLSearchParams(location.search).get('run_number');
  const [docStatus, setDocStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [user, setUser] = useState(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reviewComments, setReviewComments] = useState([]);
  const [showAIReview, setShowAIReview] = useState(false);
  const [showVerificationWarning, setShowVerificationWarning] = useState(false);
  const [pendingDownloadFormat, setPendingDownloadFormat] = useState(null);
  const [surveyVerificationStatus, setSurveyVerificationStatus] = useState({ pending: false, count: 0 });
  const [serviceBDelivery, setServiceBDelivery] = useState(serviceBCaseId ? 'pending' : 'none');
  const surveyVerificationRef = useRef(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      console.log('DownloadPage - User loaded:', parsedUser);
      console.log('DownloadPage - User role:', parsedUser?.role);
    }
    fetchDocumentStatus();
  }, [documentId]);

  const registerServiceBDelivery = async () => {
    if (!serviceBCaseId) return;
    setServiceBDelivery('saving');
    try {
      await axios.post(`${API}/counsel/cases/${serviceBCaseId}/opinions/register-generated`, { generated_opinion_id: documentId });
      setServiceBDelivery('saved');
      toast.success('Generated opinion saved privately to the Service B case.');
    } catch (error) {
      setServiceBDelivery('error');
      toast.error(error.response?.data?.detail || 'The generated opinion could not be saved to the Service B case.');
    }
  };

  useEffect(() => {
    if (serviceBCaseId && documentId) registerServiceBDelivery();
  }, [documentId, serviceBCaseId]);

  const fetchDocumentStatus = async () => {
    try {
      const response = await axios.get(`${API}/document-status/${documentId}`);
      setDocStatus(response.data);
      console.log('DownloadPage - Document status:', response.data.status);
      
      // SURVEY GATE: Check verification status
      try {
        const verifyResponse = await axios.get(`${API}/survey-verification/${documentId}/status`);
        const unverifiedCount = verifyResponse.data?.unverified_count || 0;
        setSurveyVerificationStatus({
          pending: unverifiedCount > 0,
          count: unverifiedCount
        });
        console.log('DownloadPage - Survey verification:', verifyResponse.data);
      } catch (verifyError) {
        // If no verification data exists, assume all verified
        console.log('No survey verification data found, assuming verified');
        setSurveyVerificationStatus({ pending: false, count: 0 });
      }
      
      // Fetch review comments if status is rejected or any review status
      if (response.data.status === 'rejected' || response.data.status === 'pending_review' || response.data.status === 'approved') {
        try {
          const commentsResponse = await axios.get(`${API}/opinions/${documentId}/comments`);
          setReviewComments(commentsResponse.data.comments || []);
        } catch (err) {
          console.error('Error fetching comments:', err);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load document");
      navigate("/");
    }
  };

  const handleSubmitForReview = async () => {
    if (!window.confirm('Submit this opinion for review? You will be notified once it is reviewed.')) {
      return;
    }

    try {
      setSubmittingReview(true);
      await axios.post(`${API}/opinions/submit-for-review`, {
        document_id: documentId
      });
      
      toast.success('Opinion submitted for review successfully!');
      fetchDocumentStatus(); // Refresh to show new status
    } catch (error) {
      console.error('Error submitting for review:', error);
      const errorMsg = getErrorMessage(error, 'Failed to submit for review');
      toast.error(errorMsg);
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleUploadEdited = async (file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.docx')) {
      toast.error('Please upload a .docx file');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      toast.info('Uploading edited document...');
      
      const response = await axios.post(
        `${API}/documents/${documentId}/upload-edited`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      setHasUploaded(true);
      toast.success('✅ Edited document uploaded successfully! You can now submit for review.');
      fetchDocumentStatus(); // Refresh to show updated version
    } catch (error) {
      console.error('Error uploading edited document:', error);
      const errorMsg = getErrorMessage(error, 'Failed to upload edited document');
      toast.error(errorMsg);
      setHasUploaded(false);
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, text: 'Draft' },
      pending_review: { color: 'bg-orange-100 text-orange-800', icon: Send, text: 'Pending Review' },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, text: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Rejected' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full ${config.color} font-medium`}>
        <Icon className="w-4 h-4" />
        <span>{config.text}</span>
      </div>
    );
  };

  const handleDownload = async (format) => {
    // SURVEY GATE: Check if verification is pending
    if (surveyVerificationStatus.pending) {
      setPendingDownloadFormat(format);
      setShowVerificationWarning(true);
      return;
    }
    
    // Proceed with download
    await performDownload(format);
  };
  
  const performDownload = async (format) => {
    try {
      toast.info(`Downloading ${format.toUpperCase()}...`);
      
      const downloadUrl = `${API}/download/${documentId}/${format}`;
      
      // Check if we're in a sandboxed iframe (Emergent preview)
      const isInSandboxedFrame = window.self !== window.top;
      
      if (isInSandboxedFrame) {
        // For sandboxed iframes, open download URL in a new tab
        // This bypasses the sandbox restrictions
        window.open(downloadUrl, '_blank');
        toast.success(`${format.toUpperCase()} download initiated in new tab!`);
        return;
      }
      
      // Standard download approach for non-sandboxed environments
      // Use axios to download as blob
      const response = await axios.get(downloadUrl, {
        responseType: 'blob'
      });

      // Create blob with correct MIME type
      const blob = new Blob([response.data], { 
        type: response.headers['content-type']
      });
      
      // Use the FileSaver pattern that always works
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `legal_opinion_${docStatus.bank_format}.${format}`;
      
      // Important: Must append to body for Firefox
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Important: Wait before cleanup for Safari
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 0);
      
      toast.success(`${format.toUpperCase()} downloaded!`);
      
    } catch (error) {
      console.error('Download error:', error);

      let errorMessage = `Failed to download ${format.toUpperCase()}. Please try again.`;
      const errorBlob = error?.response?.data;

      if (errorBlob instanceof Blob) {
        try {
          const text = await errorBlob.text();
          const parsed = JSON.parse(text);
          errorMessage = parsed?.detail || parsed?.message || errorMessage;
        } catch {
          errorMessage = error?.message || errorMessage;
        }
      } else if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  };
  
  const handleProceedWithDownload = () => {
    setShowVerificationWarning(false);
    if (pendingDownloadFormat) {
      performDownload(pendingDownloadFormat);
      setPendingDownloadFormat(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto mb-4"></div>
          <p className="text-gray-600">Preparing your document...</p>
        </div>
      </div>
    );
  }

  const formatName = docStatus?.bank_format === 'lic' ? 'LIC' :
                     docStatus?.bank_format === 'indusind' ? 'IndusInd Bank' :
                     docStatus?.bank_format === 'kotak' ? 'Kotak Mahindra Bank' :
                     docStatus?.bank_format === 'pinkcity' ? 'Pink City Finance' :
                     docStatus?.bank_format === 'samunnati' ? 'Samunnati' :
                     docStatus?.bank_format === 'apf' ? 'APF' :  // Keep for backward compatibility with APF workflow
                     docStatus?.bank_format === 'private' ? 'Private Opinion' : 'Bank';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>Your Legal Opinion is Ready!</h1>
              <p className="text-sm text-gray-600">Download your signed document in your preferred format</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Survey Verification Button - Phase 2.3 */}
              {/* Pass documentId (opinion_id) since anchors are stored with opinion_id as case_id */}
              <SurveyVerificationButton 
                ref={surveyVerificationRef}
                caseId={documentId} 
                variant="outline"
                onVerificationComplete={() => fetchDocumentStatus()}
              />
              <Button
                data-testid="home-button"
                variant="outline"
                onClick={() => navigate("/")}
              >
                <Home className="w-4 h-4 mr-2" />
                Create New Opinion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Step Indicator */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="text-sm font-medium text-gray-600">Upload</span>
            </div>
            <div className="w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="text-sm font-medium text-gray-600">Review</span>
            </div>
            <div className="w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="text-sm font-medium text-gray-600">Signature</span>
            </div>
            <div className="w-16 h-0.5 bg-green-500"></div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-semibold">✓</div>
              <span className="text-sm font-medium text-green-600">Download</span>
            </div>
          </div>

          {/* Success Message */}
          <Card data-testid="download-card" className="shadow-xl mb-6">
            <CardContent className="pt-8 pb-8">
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                </div>
                <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>Document Ready!</h2>
                <p className="text-lg text-gray-600">Your legal opinion for <span className="font-semibold">{formatName}</span> has been successfully generated with your digital signature</p>
                
                {/* Opinion Status */}
                <div className="mt-4 flex items-center gap-3">
                  {getStatusBadge(docStatus?.status || 'draft')}
                  {docStatus?.version && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      v{docStatus.version}
                    </span>
                  )}
                </div>

                {/* Review Comments if Rejected */}
                {docStatus?.status === 'rejected' && (
                  <div className="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-lg text-left">
                    <p className="text-sm font-bold text-red-900 mb-3 flex items-center">
                      <AlertCircle className="w-5 h-5 mr-2" />
                      Opinion Rejected - Reviewer Feedback
                    </p>
                    
                    {reviewComments.length > 0 ? (
                      <div className="space-y-3">
                        {reviewComments.map((comment, index) => (
                          <div key={comment.id || index} className="bg-white p-3 rounded border border-red-200">
                            <div className="flex justify-between items-start mb-1">
                              <span className="text-xs font-semibold text-red-800">{comment.reviewer_name}</span>
                              <span className="text-xs text-red-600">{new Date(comment.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-sm text-gray-800">{comment.comment_text}</p>
                          </div>
                        ))}
                      </div>
                    ) : docStatus?.review_comments && (
                      <div className="bg-white p-3 rounded border border-red-200">
                        <p className="text-sm text-gray-800">{docStatus.review_comments}</p>
                        <p className="text-xs text-red-600 mt-2">Reviewed by: {docStatus.reviewer_name}</p>
                      </div>
                    )}
                    
                    <div className="mt-3 pt-3 border-t border-red-200">
                      <p className="text-xs font-semibold text-red-900">📋 Next Steps:</p>
                      <ol className="text-xs text-red-800 mt-1 ml-4 list-decimal space-y-1">
                        <li>Download the document below</li>
                        <li>Make corrections based on reviewer feedback</li>
                        <li>Upload the corrected document</li>
                        <li>Resubmit for review</li>
                      </ol>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* AI Review Panel Button */}
          <div className="max-w-2xl mx-auto mb-6">
            <Card className="border-2 border-purple-400 hover:shadow-lg transition-all bg-gradient-to-r from-purple-50 to-blue-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center">
                      <Bot className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">AI Legal Review</h3>
                      <p className="text-xs text-gray-600">Get AI-powered analysis before signing</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => setShowAIReview(true)}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Bot className="w-4 h-4 mr-2" />
                    Review Opinion
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Download and Upload Options */}
          <div className="max-w-2xl mx-auto mb-6 space-y-4">
            {serviceBCaseId && <Card className="border-2 border-emerald-400" data-testid="service-b-generated-opinion-delivery-card"><CardHeader className="bg-emerald-50"><CardTitle>Service B delivery</CardTitle><CardDescription data-testid="service-b-generated-opinion-run">Run {serviceBRunNumber || 'linked to this paid case'}</CardDescription></CardHeader><CardContent className="pt-5"><p className="text-sm text-slate-700" data-testid="service-b-generated-opinion-delivery-status">{serviceBDelivery === 'saving' ? 'Saving the generated opinion privately…' : serviceBDelivery === 'saved' ? 'Saved privately. Return to Counsel to review and Send to BRD.' : serviceBDelivery === 'error' ? 'The generated opinion was not saved yet.' : 'Preparing private Service B delivery.'}</p><div className="mt-4 flex flex-wrap gap-3">{serviceBDelivery === 'error' ? <Button type="button" onClick={registerServiceBDelivery} data-testid="service-b-retry-save-generated-opinion-button">Save privately</Button> : null}<Button type="button" variant="outline" onClick={() => navigate('/counsel/home')} data-testid="service-b-return-to-counsel-button">Return to Counsel</Button></div></CardContent></Card>}
            {/* Step 1: Download DOCX */}
            <Card data-testid="download-docx-card" className="border-2 border-blue-400 hover:shadow-lg transition-all">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardTitle className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                  <Download className="w-6 h-6 text-blue-700" />
                  <span>Download Legal Opinion</span>
                </CardTitle>
                <CardDescription>Download the document to edit in MS Word</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <Button
                  data-testid="download-docx-button"
                  onClick={() => handleDownload('docx')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download DOCX
                </Button>
                <ul className="text-xs text-gray-600 mt-3 space-y-1">
                  <li>✓ Editable in MS Word</li>
                  <li>✓ Contains all points with table format</li>
                  <li>✓ Includes your digital signature</li>
                  <li>✓ Ready for editing and printing</li>
                </ul>
              </CardContent>
            </Card>

            {/* Step 2: Upload Edited Document */}
            <Card className={`border-2 hover:shadow-lg transition-all ${hasUploaded ? 'border-green-400 bg-green-50' : 'border-purple-400'}`}>
              <CardHeader className={`bg-gradient-to-r ${hasUploaded ? 'from-green-50 to-green-100' : 'from-purple-50 to-pink-50'}`}>
                <CardTitle className="flex items-center space-x-2">
                  <div className={`w-8 h-8 ${hasUploaded ? 'bg-green-600' : 'bg-purple-600'} text-white rounded-full flex items-center justify-center font-bold`}>
                    {hasUploaded ? '✓' : '2'}
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className={`w-6 h-6 ${hasUploaded ? 'text-green-700' : 'text-purple-700'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>Upload Edited Document</span>
                  {hasUploaded && <span className="text-green-600 font-semibold ml-2">✓ Uploaded</span>}
                </CardTitle>
                <CardDescription>
                  {hasUploaded ? 'Document uploaded successfully! You can now submit for review.' : 'After editing in Word, upload the updated version here'}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <input
                  type="file"
                  accept=".docx"
                  onChange={(e) => handleUploadEdited(e.target.files[0])}
                  className="hidden"
                  id="upload-edited"
                  disabled={uploading}
                />
                <label htmlFor="upload-edited" className="block cursor-pointer">
                  <div className={`w-full ${uploading ? 'bg-gray-400' : hasUploaded ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'} text-white py-6 text-lg font-semibold rounded-lg text-center transition-colors flex items-center justify-center space-x-2`}>
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Uploading...</span>
                      </>
                    ) : hasUploaded ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Upload Different Version</span>
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span>Upload Edited DOCX</span>
                      </>
                    )}
                  </div>
                </label>
                <ul className="text-xs text-gray-600 mt-3 space-y-1">
                  <li>✓ Upload your modified Word document</li>
                  <li>✓ Replaces the original with your edits</li>
                  <li>✓ Version tracking maintained</li>
                </ul>
              </CardContent>
            </Card>

            {/* Step 3: Submit for Review - At Bottom, Only Enabled After Upload */}
            {(docStatus?.status === 'draft' || docStatus?.status === 'rejected') && (
              <Card className={`border-2 ${!hasUploaded ? 'border-gray-300 opacity-60' : 'border-orange-400'} hover:shadow-lg transition-all`}>
                <CardHeader className={`bg-gradient-to-r ${!hasUploaded ? 'from-gray-50 to-gray-100' : 'from-orange-50 to-yellow-50'}`}>
                  <CardTitle className="flex items-center space-x-2">
                    <div className={`w-8 h-8 ${!hasUploaded ? 'bg-gray-400' : 'bg-orange-600'} text-white rounded-full flex items-center justify-center font-bold`}>3</div>
                    <Send className={`w-6 h-6 ${!hasUploaded ? 'text-gray-400' : 'text-orange-700'}`} />
                    <span className={!hasUploaded ? 'text-gray-500' : 'text-orange-800'}>Submit for Review</span>
                  </CardTitle>
                  <CardDescription>
                    {!hasUploaded ? 'Please upload edited document first before submitting' : 'Send this opinion to reviewers for approval'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <Button
                    onClick={handleSubmitForReview}
                    disabled={submittingReview || !hasUploaded}
                    className={`w-full py-6 text-lg font-semibold ${!hasUploaded ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700'} text-white`}
                  >
                    {submittingReview ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : !hasUploaded ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Upload Document to Unlock
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit for Review
                      </>
                    )}
                  </Button>
                  <div className={`text-sm mt-3 text-center ${!hasUploaded ? 'text-gray-500' : 'text-gray-600'}`}>
                    {!hasUploaded ? (
                      <p>⚠️ You must upload the edited document before submitting</p>
                    ) : (
                      <>
                        <p>✓ Opinion will be sent to reviewers for approval</p>
                        <p>✓ You will be notified once it is reviewed</p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Next Steps */}
          <Card className="bg-gradient-to-r from-gray-50 to-blue-50">
            <CardHeader>
              <CardTitle>What to do next?</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  <span>Review the downloaded document for completeness and accuracy</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  <span>Print on your official letterhead if required by the bank</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  <span>Add your official seal or stamp as per bank requirements</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  <span>Submit the final document to the bank or concerned party</span>
                </li>
              </ul>

              {/* Direct Download Links for Troubleshooting */}
              <div className="mt-6 pt-6 border-t-2 border-gray-300">
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                  <p className="text-sm font-bold text-gray-900 mb-3">⚠️ If download does not start automatically, use this direct link:</p>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500 mb-1 font-semibold">Word Document:</p>
                    <a 
                      href={`${API}/download/${documentId}/docx`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm break-all underline"
                    >
                      Right-click this link → Save as... (DOCX)
                    </a>
                  </div>
                  <p className="text-xs text-gray-600 mt-3 italic">
                    💡 Right-click on the link above and select Save link as... to manually download the file
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      
      {/* AI Review Panel */}
      <AIReviewPanel 
        opinionId={docStatus?.opinion_id || documentId}
        isOpen={showAIReview}
        onClose={() => setShowAIReview(false)}
        onApplySuggestionComplete={() => {
          // Refresh the document status to show new version
          fetchDocumentStatus();
        }}
      />
      
      {/* SURVEY GATE: Verification Warning Dialog */}
      <Dialog open={showVerificationWarning} onOpenChange={setShowVerificationWarning}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <TriangleAlert className="w-5 h-5" />
              Survey Verification Pending
            </DialogTitle>
            <DialogDescription className="text-left">
              Survey verification is recommended before final download.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2" data-testid="survey-verification-warning-content">
            <p className="text-gray-900">
              <strong>{surveyVerificationStatus.count} survey number(s)</strong> in this document require verification before final submission to the bank.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-sm text-orange-800">
                <strong>Recommended:</strong> Click Verify Surveys to review and confirm survey numbers before downloading the final document.
              </p>
            </div>
            <p className="text-sm text-gray-600">
              You can still proceed with download, but the document may contain survey numbers that need correction.
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowVerificationWarning(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowVerificationWarning(false);
                // Use ref to open the verification panel
                if (surveyVerificationRef.current) {
                  surveyVerificationRef.current.openPanel();
                }
              }}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Verify Surveys
            </Button>
            <Button
              variant="destructive"
              onClick={handleProceedWithDownload}
              className="w-full sm:w-auto"
            >
              Proceed Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DownloadPage;